import type {
  StaffAvailability,
  StaffResponse,
  WeekDateRangeResponse,
  WorkDay
} from '../api/generated';
import type { DayKey } from '../constants/weekDayConstants';
import { DAY_ORDER } from '../constants/weekDayConstants';
import { addDaysToIsoDate, isoStringToDate } from '../utils/dateUtils';

const MINUTES_PER_HOUR = 60;

type WeekSchedule = Partial<Record<DayKey, WorkDay>>;

const toMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * MINUTES_PER_HOUR + minutes;
};

// A malformed or inverted range contributes nothing rather than negative hours.
const workDayHours = (day: WorkDay): number => {
  if (!day.startTime || !day.endTime) return 0;
  const minutes = toMinutes(day.endTime) - toMinutes(day.startTime);
  return minutes > 0 ? minutes / MINUTES_PER_HOUR : 0;
};

// Staff saved before the even/odd split kept the schedule directly on
// availability.weekSchedule — same precedence as staffAvailabilityToForm.
export const resolveWeekSchedule = (
  availability: StaffAvailability,
  isEvenWeek: boolean
): WeekSchedule => {
  const parityWeek = isEvenWeek ? availability.evenWeeks : availability.oddWeeks;
  return parityWeek?.weekSchedule ?? availability.weekSchedule ?? {};
};

// Employment is prorated per day: an enabled schedule day only counts when its
// calendar date falls inside the member's employment period, so a Wednesday
// start contributes Wed–Fri of that week rather than the full week.
const isEmployedOn = (member: StaffResponse, isoDate: string): boolean =>
  member.startDate <= isoDate && (!member.endDate || member.endDate >= isoDate);

// Date.getDay() is Sunday-first (0); DAY_ORDER is Monday-first.
const dayKeyOf = (date: Date): DayKey => DAY_ORDER[(date.getDay() + 6) % 7];

// Walks the week's actual dates rather than offsetting from startDate: the
// year's first and last week are clamped to the calendar year (the backend's
// IsoWeekDateRangeUtil), so startDate is not always a Monday and the range can
// span fewer — or, for a clamped week 1, more — than seven days.
const weeklyHoursFor = (
  member: StaffResponse,
  schedule: WeekSchedule,
  week: WeekDateRangeResponse
): number => {
  let hours = 0;
  for (let iso = week.startDate; iso <= week.endDate; iso = addDaysToIsoDate(iso, 1)) {
    const date = isoStringToDate(iso);
    // An unparsable date would also no longer advance — bail instead of looping.
    if (!date) break;
    const workDay = schedule[dayKeyOf(date)];
    if (workDay && isEmployedOn(member, iso)) {
      hours += workDayHours(workDay);
    }
  }
  return hours;
};

// Weekly capacity per staff profession: the configured working hours of every
// member holding that profession, resolved against the week's parity (Danish
// lige/ulige uger follow the ISO week number). A member's full hours count
// toward each of their professions — multi-skilled staff are available to
// every competence they hold, not split between them.
export const calculateWeeklyCompetenceCapacityHours = (
  staff: readonly StaffResponse[],
  week: WeekDateRangeResponse
): Map<string, number> => {
  const isEvenWeek = week.weekNumber % 2 === 0;
  const capacity = new Map<string, number>();
  for (const member of staff) {
    const schedule = resolveWeekSchedule(member.availability, isEvenWeek);
    const weeklyHours = weeklyHoursFor(member, schedule, week);
    if (weeklyHours === 0) continue;
    for (const professionId of member.staffProfessionIds) {
      capacity.set(professionId, (capacity.get(professionId) ?? 0) + weeklyHours);
    }
  }
  return capacity;
};
